import React from 'react';
import { Box, Typography, Chip, useTheme, alpha } from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  FolderOpen,
  CheckCircle,
  Schedule,
  ErrorOutline,
} from '@mui/icons-material';
import AnimatedCard from '../common/AnimatedCard';
import { motion } from 'framer-motion';
import { DashboardStats as DashboardStatsType } from '../../types/medicalDataList';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  trend?: number;
  color: 'primary' | 'success' | 'warning' | 'error';
  delay?: number;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, trend, color, delay = 0 }) => {
  const theme = useTheme();
  const isPositiveTrend = trend && trend > 0;

  return (
    <AnimatedCard
      delay={delay}
      hover
      glow
      sx={{
        p: 3,
        background: `linear-gradient(135deg, ${alpha(
          theme.palette[color].main,
          0.05
        )} 0%, transparent 100%)`,
        borderTop: `4px solid ${theme.palette[color].main}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Box
            sx={{
              display: 'inline-flex',
              p: 1.5,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette[color].main, 0.1),
              color: theme.palette[color].main,
              mb: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: delay + 0.2, type: 'spring', stiffness: 200 }}
          >
            <Typography variant="h3" fontWeight="bold" color="text.primary">
              {value.toLocaleString()}
            </Typography>
          </motion.div>
        </Box>
        {trend !== undefined && (
          <Chip
            size="small"
            icon={isPositiveTrend ? <TrendingUp /> : <TrendingDown />}
            label={`${isPositiveTrend ? '+' : ''}${trend.toFixed(1)}%`}
            sx={{
              backgroundColor: alpha(
                isPositiveTrend ? theme.palette.success.main : theme.palette.error.main,
                0.1
              ),
              color: isPositiveTrend ? theme.palette.success.main : theme.palette.error.main,
              fontWeight: 600,
              '& .MuiChip-icon': {
                fontSize: 16,
                color: 'inherit',
              },
            }}
          />
        )}
      </Box>
    </AnimatedCard>
  );
};

interface DashboardStatsProps {
  stats: DashboardStatsType | null;
  loading?: boolean;
}

const DashboardStats: React.FC<DashboardStatsProps> = ({ stats, loading }) => {
  if (loading || !stats) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
        {[...Array(4)].map((_, index) => (
          <AnimatedCard key={index} sx={{ height: 160 }} />
        ))}
      </Box>
    );
  }

  const calculateTrend = (current: number, previous: number) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const statCards = [
    {
      title: '総ファイル数',
      value: stats.totalFiles,
      icon: <FolderOpen />,
      trend: calculateTrend(stats.totalFiles, stats.totalFiles - 10), // 仮の値
      color: 'primary' as const,
    },
    {
      title: '完了',
      value: stats.completedFiles,
      icon: <CheckCircle />,
      trend: calculateTrend(stats.completedFiles, stats.completedFiles - 5),
      color: 'success' as const,
    },
    {
      title: '処理中',
      value: stats.processingFiles,
      icon: <Schedule />,
      trend: calculateTrend(stats.processingFiles, stats.processingFiles + 2),
      color: 'warning' as const,
    },
    {
      title: 'エラー',
      value: stats.errorFiles,
      icon: <ErrorOutline />,
      trend: calculateTrend(stats.errorFiles, stats.errorFiles - 1),
      color: 'error' as const,
    },
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
      {statCards.map((stat, index) => (
        <StatCard key={stat.title} {...stat} delay={index * 0.1} />
      ))}
    </Box>
  );
};

export default DashboardStats;